import { ConnectedRouter } from 'connected-react-router';
import memoizeOne from 'memoize-one';
import QueryString from 'query-string';
import React, { PureComponent } from 'react';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import AccessTokenRemounter from '~/AccessTokenRemounter';
import { OrderDirection, SortField } from '~/Api/Product';
import { DataSourcesPage } from '~/pages/DataSourceStatus/';
import { Download } from '~/Download';
import FlashMessage from '~/FlashMessage';
import { HelpPage } from '~/Help';
import Layout from '~/Layout';
import parseUuid from '~/utils/Uuid/parseUuid';
import { LoginPage } from '~/Login';
import { GdprPage } from '~/Login/GdprPage';
import { Page as MedicinalBrandsAnalyticsPage } from '~/MedicinalBrandsAnalytics';
import { PasswordChangePage } from '~/PasswordChange';
import { PasswordResetPage } from '~/PasswordReset';
import { DetailPage as ProductGroupDetailPage } from '~/ProductGroupDetail';
import { ListPage as ProductGroupListPage } from '~/ProductGroupList';
import { SearchPage } from '~/ProductSearch';
import { UserSettings } from '~/RoMaxPrice';
import NavigationMenu from '~/UI/NavigationMenu';
import NotFoundErrorPage from '~/UI/NotFoundErrorPage/NotFoundErrorPage';
import { ImpersonatePage } from '~/UserImpersonate';
import { Panel as UserImpersonatePanel } from '~/UserImpersonatePanel';
import { UserNavigationItem } from '~/UserNavigationItem';
import { UserProfilePage } from '~/UserProfile';
import { History } from 'history';

import parseUrlCurrency from './parseUrlCurrency';
import parseUrlPriceType from './parseUrlPriceType';
import { parseBrandDetailUrlParams } from '~/MedicinalBrandsAnalytics/VirtualBrand/BrandDetailUrlState';
import AccessTokenRequester from '~/AccessToken/AccessTokenRequester/AccessTokenRequester';
import { InvitationAcceptPage } from '~/InvitationAccept/InvitationAcceptPage';
import { Notifications } from '~/pages/Notifications';
import { NotificationsDetail } from '~/pages/NotificationsDetail';
import { IRPHelpPage } from '~/pages/IRPHelp';
import { IRPDetailPage } from '~/pages/IRPDetail';
import { IRPPage } from '~/pages/IRP';
import { AvailabilityPage } from '~/pages/Availability';
import { AvailabilityMetodikaPage } from '~/pages/Availability/MetodikaPage';
import ERPSessionRedirect from '~/ERP/ERPSessionRedirect';
import { FilterOperator } from '~/Api/Product/Query';

const ProductDetailModalTabNameRegexp = 'history|price|source';
const parseUrlQuery = memoizeOne(QueryString.parse);

interface Props {
	history: History;
}

export default class Router extends PureComponent<Props> {
	render() {
		return (
			<ConnectedRouter history={this.props.history}>
				<Switch>
					<Route
						exact
						path='/login'
						render={() => (
							<Layout>
								<LoginPage />
							</Layout>
						)}
					/>

					<Route
						exact
						path='/gdpr-consent'
						render={() => (
							<AccessTokenRemounter>
								<Layout>
									<GdprPage />
								</Layout>
							</AccessTokenRemounter>
						)}
					/>

					<Route
						exact
						path='/profile'
						render={() => (
							<Layout bottomNavigationPanel={<UserImpersonatePanel />}>
								<UserProfilePage />
							</Layout>
						)}
					/>

					<Route
						exact
						path='/download/:fileId/:fileName'
						render={(props) => {
							const { fileId, fileName } = props.match.params;
							return (
								<Layout
									bottomNavigationPanel={<UserImpersonatePanel />}
									navigation={<NavigationMenu />}
									navigationPanel={<UserNavigationItem />}
								>
									<Download fileId={fileId} fileName={fileName} />
								</Layout>
							);
						}}
					/>

					<Route
						exact
						path='/profile/settings/ro-max-price'
						render={() => (
							<Layout bottomNavigationPanel={<UserImpersonatePanel />}>
								<UserSettings />
							</Layout>
						)}
					/>

					<Route
						exact
						path='/accept-invitation'
						render={(props) => {
							const { user: userId, token: token } = parseUrlQuery(props.location.search);
							const parsedUserId = parseUuid(userId);
							const parsedToken = parseUuid(token);

							return (
								<Layout>
									<InvitationAcceptPage token={parsedToken} userId={parsedUserId} />
								</Layout>
							);
						}}
					/>

					<Route
						exact
						path='/change-password'
						render={(props: RouteComponentProps<any>) => {
							const { token, user: userId } = parseUrlQuery(props.location.search);
							return (
								<Layout>
									<PasswordChangePage
										redirectUrl={props.location.pathname}
										token={(Array.isArray(token) ? token[0] : token) || null}
										userId={parseUuid(userId)}
									/>
								</Layout>
							);
						}}
					/>

					<Route
						exact
						path='/reset-password'
						render={() => (
							<Layout>
								<PasswordResetPage />
							</Layout>
						)}
					/>

					<Route
						exact
						path='/impersonate'
						render={(props: RouteComponentProps<any>) => {
							const { targetUserId } = parseUrlQuery(props.location.search);
							return (
								<Layout>
									<ImpersonatePage targetUserId={parseUuid(targetUserId)} />
								</Layout>
							);
						}}
					/>

					<Route
						exact
						path='/analytics'
						render={(routeProps) => {
							const { filter, currency } = parseUrlQuery(routeProps.location.search);
							const parsedCurrency = parseUrlCurrency(currency);
							const parsedUrlDefaults = parseBrandDetailUrlParams(routeProps.location.search);

							return (
								<AccessTokenRemounter>
									<Layout
										bottomNavigationPanel={<UserImpersonatePanel />}
										navigation={<NavigationMenu />}
										navigationPanel={<UserNavigationItem />}
									>
										<MedicinalBrandsAnalyticsPage
											currency={parsedCurrency}
											filter={filter as string[]}
											parsedUrlDefaults={parsedUrlDefaults}
										/>
									</Layout>
								</AccessTokenRemounter>
							);
						}}
					/>

					<Route
						exact
						path={`/search/:productId?/:productTabName(${ProductDetailModalTabNameRegexp})?`}
						render={(routeProps) => {
							const {
								sort,
								operator,
								order,
								currency,
								price,
								'no-calc-price': filterCalculatedPrice,
								filter,
							} = parseUrlQuery(routeProps.location.search);
							const parsedCurrency = parseUrlCurrency(currency);

							return (
								<AccessTokenRemounter>
									<Layout
										bottomNavigationPanel={<UserImpersonatePanel />}
										navigation={<NavigationMenu />}
										navigationPanel={<UserNavigationItem />}
									>
										<SearchPage
											currency={parsedCurrency}
											filter={filter}
											filterCalculatedPrice={filterCalculatedPrice === '1'}
											location={routeProps.location}
											filterOperator={operator as FilterOperator | null}
											order={order as OrderDirection | null}
											priceType={parseUrlPriceType(price)}
											productId={parseUuid(routeProps.match.params.productId)}
											productTabName={routeProps.match.params.productTabName}
											sort={sort as SortField | null}
										/>
									</Layout>
								</AccessTokenRemounter>
							);
						}}
					/>

					<Route
						exact
						path='/product-groups/'
						render={(props) => (
							<AccessTokenRemounter>
								<Layout
									bottomNavigationPanel={<UserImpersonatePanel />}
									navigation={<NavigationMenu />}
									navigationPanel={<UserNavigationItem />}
								>
									{/*  TODO: resolve after bumping react-router-dom */}
									<ProductGroupListPage location={props.location as any} />
								</Layout>
							</AccessTokenRemounter>
						)}
					/>

					<Route
						exact
						path={`/product-groups/:groupId/:productId?/:productTabName(${ProductDetailModalTabNameRegexp})?`}
						render={(routeProps) => {
							const {
								currency,
								price,
								'no-calc-price': filterCalculatedPrice,
								'notification-settings': showNotificationSettings,
								'show-history': showHistory,
							} = parseUrlQuery(routeProps.location.search);
							const parsedCurrency = parseUrlCurrency(currency);

							return (
								<AccessTokenRemounter>
									<Layout
										bottomNavigationPanel={<UserImpersonatePanel />}
										navigation={<NavigationMenu />}
										navigationPanel={<UserNavigationItem />}
									>
										<ProductGroupDetailPage
											{...routeProps}
											currency={parsedCurrency}
											filterCalculatedPrice={filterCalculatedPrice === '1'}
											groupId={parseUuid(routeProps.match.params.groupId)}
											priceType={parseUrlPriceType(price)}
											productId={parseUuid(routeProps.match.params.productId)}
											productTabName={routeProps.match.params.productTabName}
											showHistory={showHistory === '1'}
											showNotificationSettings={showNotificationSettings === '1'}
										/>
									</Layout>
								</AccessTokenRemounter>
							);
						}}
					/>

					<Route
						exact
						path='/help/irp'
						render={() => (
							<AccessTokenRemounter>
								<Layout
									bottomNavigationPanel={<UserImpersonatePanel />}
									navigation={<NavigationMenu />}
									navigationPanel={<UserNavigationItem />}
								>
									<IRPHelpPage />
								</Layout>
							</AccessTokenRemounter>
						)}
					/>

					<Route
						exact
						path='/help'
						render={(routeProps) => (
							<AccessTokenRemounter>
								<Layout
									bottomNavigationPanel={<UserImpersonatePanel />}
									navigation={<NavigationMenu />}
									navigationPanel={<UserNavigationItem />}
								>
									<HelpPage scrollOnExport={routeProps.location.hash === '#export'} />
								</Layout>
							</AccessTokenRemounter>
						)}
					/>

					<Route
						exact
						path='/datasources'
						render={() => (
							<AccessTokenRemounter>
								<Layout
									bottomNavigationPanel={<UserImpersonatePanel />}
									navigation={<NavigationMenu />}
									navigationPanel={<UserNavigationItem />}
								>
									<section style={{ fontSize: 'initial', overflow: 'scroll', width: '100%', height: '100%' }}>
										<div className='w-full h-full'>
											<DataSourcesPage />
										</div>
									</section>
								</Layout>
							</AccessTokenRemounter>
						)}
					/>

					<Route
						exact
						path='/irp/brand/:virtualBrandId/pack/:virtualPackId'
						render={(routeProps) => {
							return (
								<AccessTokenRequester>
									<AccessTokenRemounter>
										<ERPSessionRedirect
											virtualBrandId={routeProps.match.params.virtualBrandId}
											virtualPackId={routeProps.match.params.virtualPackId}
										/>
										<FlashMessage />
									</AccessTokenRemounter>
								</AccessTokenRequester>
							);
						}}
					/>

					<Route
						exact
						path='/irp/pack/:erpPackId/session/:erpSessionId'
						render={(routeProps) => {
							const { currency } = parseUrlQuery(routeProps.location.search);

							return (
								<AccessTokenRequester>
									<AccessTokenRemounter>
										<Layout
											bottomNavigationPanel={<UserImpersonatePanel />}
											navigation={<NavigationMenu />}
											navigationPanel={<UserNavigationItem />}
										>
											<IRPDetailPage
												defaultCurrency={parseUrlCurrency(currency)}
												erpPackId={routeProps.match.params.erpPackId}
												erpSessionId={routeProps.match.params.erpSessionId}
											/>
										</Layout>
										<FlashMessage />
									</AccessTokenRemounter>
								</AccessTokenRequester>
							);
						}}
					/>

					<Route
						exact
						path='/notifications/:section/:subsection'
						render={() => (
							<div style={{ overflow: 'scroll', height: '100%' }}>
								<AccessTokenRequester>
									<AccessTokenRemounter>
										<NotificationsDetail />
									</AccessTokenRemounter>
								</AccessTokenRequester>
							</div>
						)}
					/>
					<Route
						exact
						path='/notifications'
						render={() => (
							<AccessTokenRequester>
								<AccessTokenRemounter>
									<Layout
										bottomNavigationPanel={<UserImpersonatePanel />}
										navigation={<NavigationMenu />}
										navigationPanel={<UserNavigationItem />}
									>
										{/* NOTE: fontSize: initial - override font-size from reset.less for tailwind */}
										<section style={{ fontSize: 'initial', overflow: 'scroll', width: '100%', height: '100%' }}>
											<div className='w-full h-full' data-testid='notifications-feed-container'>
												<Notifications />
											</div>
										</section>
									</Layout>
								</AccessTokenRemounter>
							</AccessTokenRequester>
						)}
					/>

					<Route
						exact
						path='/irp'
						render={() => {
							return (
								<AccessTokenRequester>
									<AccessTokenRemounter>
										<Layout
											bottomNavigationPanel={<UserImpersonatePanel />}
											navigation={<NavigationMenu />}
											navigationPanel={<UserNavigationItem />}
										>
											<IRPPage />
										</Layout>
									</AccessTokenRemounter>
								</AccessTokenRequester>
							);
						}}
					/>

					<Route
						exact
						path='/availability'
						render={() => (
							<AccessTokenRequester>
								<AccessTokenRemounter>
									<Layout
										bottomNavigationPanel={<UserImpersonatePanel />}
										navigation={<NavigationMenu />}
										navigationPanel={<UserNavigationItem />}
									>
										<AvailabilityPage />
									</Layout>
								</AccessTokenRemounter>
							</AccessTokenRequester>
						)}
					/>

					<Route
						exact
						path='/availability/metodika'
						render={() => (
							<AccessTokenRequester>
								<AccessTokenRemounter>
									<Layout
										bottomNavigationPanel={<UserImpersonatePanel />}
										navigation={<NavigationMenu />}
										navigationPanel={<UserNavigationItem />}
									>
										<AvailabilityMetodikaPage />
									</Layout>
								</AccessTokenRemounter>
							</AccessTokenRequester>
						)}
					/>

					<Redirect exact from='/' to='/product-groups/' />

					<Route
						render={(props) => (
							<Layout navigation={<NavigationMenu />}>
								<NotFoundErrorPage {...props} />
							</Layout>
						)}
					/>
				</Switch>
			</ConnectedRouter>
		);
	}
}
